import { gql, useQuery } from '@apollo/client';
import { Avatar, Button } from '@apollo/orbit';
import classnames from 'classnames';
import React, { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router';

import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { proposalChangesRouteConfig } from 'src/app/graph/routes';
import { Loading } from 'src/components/common/loading/Loading';
import { PlainHtmlButton } from 'src/components/common/plainHtmlButton/PlainHtmlButton';
import { RelativeTimestamp } from 'src/components/timestamp/Timestamp';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { safeCompileMarkdown } from 'src/lib/safeCompileMarkdown';

const PAGE_SIZE = 25;

export const RevisionHistoryDrawer = ({
  isOpen,
  onClose,
  graphRef,
  className,
}: {
  isOpen: boolean;
  onClose: () => void;
  graphRef: GraphRef;
  className?: string;
}) => {
  const { selectedRevision } = useRouteParams(proposalChangesRouteConfig);

  const { data, loading, fetchMore } = useQuery<
    GraphQLTypes.RevisionHistoryDrawerQuery,
    GraphQLTypes.RevisionHistoryDrawerQueryVariables
  >(
    gql`
      query RevisionHistoryDrawerQuery(
        $graphId: ID!
        $graphVariant: String!
        $limit: Int!
        $offset: Int!
      ) {
        graph(id: $graphId) {
          id
          variant(name: $graphVariant) {
            id
            proposal {
              id
              revisionHistory(limit: $limit, offset: $offset) {
                totalCount
                revisions {
                  id
                  createdAt
                  createdBy {
                    id
                    ... on User {
                      fullName
                      avatarUrl
                    }
                  }
                  summary
                }
              }
            }
          }
        }
      }
    `,
    {
      variables: {
        ...graphRef,
        limit: PAGE_SIZE,
        offset: 0,
      },
    },
  );

  const history = useHistory();
  const location = useLocation();
  const onSelectRevision = useCallback(
    (revisionId: string) => () => {
      history.push(
        proposalChangesRouteConfig.locationFrom({
          location,
          patch: {
            selectedRevision: revisionId,
          },
        }),
      );
    },
    [history, location],
  );

  const revisionHistory = data?.graph?.variant?.proposal?.revisionHistory;
  const totalCount = revisionHistory?.totalCount ?? 0;
  const revisions = revisionHistory?.revisions ?? [];

  return (
    <div
      className={classnames(
        'z-30 flex h-full w-[26rem] flex-col border-l border-l-primary bg-primary 4xl:w-[37rem]',
        !isOpen && 'hidden',
        className,
      )}
    >
      <div className="flex items-center justify-between border-b border-b-primary px-1 py-2">
        <div className="pl-3 text-base font-semibold">Revision History</div>
        <Button size="sm" className="h-7" onClick={onClose}>
          Done
        </Button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 pt-4">
        {loading ? (
          <Loading />
        ) : (
          <div
            className={classnames(
              'relative mb-6 space-y-4',
              revisions.length > 1 &&
                "before:absolute before:left-[3.5px] before:top-4 before:w-px before:bg-border-disabled before:content-[''] ",
              revisions.length === totalCount
                ? 'before:bottom-20'
                : 'before:bottom-0',
            )}
          >
            {revisions.map((revision, i) => (
              <div
                key={revision.id}
                className="relative flex items-start gap-4"
              >
                <div className="relative top-4 z-10 aspect-square size-2 rounded-full border border-black bg-primary" />
                <PlainHtmlButton
                  className={classnames(
                    'focus-visible:focus-ring flex flex-1 flex-col items-start space-y-2 rounded-md p-2',
                    'hover:bg-secondary',
                    selectedRevision === revision.id && 'bg-secondary',
                  )}
                  onClick={onSelectRevision(revision.id)}
                >
                  <div className="text-base font-semibold">
                    Revision {revision.id.substring(0, 8)}{' '}
                    {i === 0 && '(current version)'}
                  </div>
                  <p className="text-left text-base text-secondary">
                    {safeCompileMarkdown(revision.summary)}
                  </p>
                  <div className="mt-2 text-sm text-secondary">
                    {revision.createdBy?.__typename === 'User' ? (
                      <>
                        <Avatar
                          src={revision.createdBy.avatarUrl ?? undefined}
                          name={revision.createdBy.fullName}
                          size="xs"
                          className="mr-2"
                        />
                        <RelativeTimestamp
                          withTooltip={false}
                          value={revision.createdAt}
                        />{' '}
                        by{' '}
                        <span className="font-semibold text-primary">
                          {revision.createdBy.fullName}
                        </span>
                      </>
                    ) : (
                      <>
                        <Avatar size="xs" className="mr-2" />
                        <RelativeTimestamp
                          withTooltip={false}
                          value={revision.createdAt}
                        />{' '}
                        by{' '}
                        <span className="font-semibold text-primary">
                          Unknown
                        </span>
                      </>
                    )}
                  </div>
                </PlainHtmlButton>
              </div>
            ))}
          </div>
        )}

        {revisions.length < totalCount && (
          <Button
            className="mb-4"
            onClick={() =>
              fetchMore({
                variables: {
                  offset: revisions.length,
                },
              })
            }
          >
            Show more revisions
          </Button>
        )}
      </div>
    </div>
  );
};
